import React, { ComponentType, useState } from 'react';
import styled from '@emotion/styled';
import { ITheme } from '../../../../../../ui';
import { GrandTotal, StyledGrandTotal } from './GrandTotal';
import { RefundTotal } from './RefundTotal';
import {
	StyledBookingSummaryFooter,
	StyledEarningLoyaltyPoints,
} from './BookingSummaryFooter';
import {
	OriginalAndNewTotalMobile,
	StyledOriginalAndNewTotalMobile,
} from './OriginalAndNewTotalMobile';
import { IPrice } from '../../../../../shared/i18n/price/IPrice';
import {
	BookingSummaryHeader,
	StyledBookingSummaryHeader,
} from './BookingSummaryHeader';
import { ParkingProductSummary } from './ParkingProductSummary';
import { AncillariesSummary } from './AncillariesSummary';
import { INewBooking } from '../../../../domain/booking/models/INewBooking';
import { StyledBookingSummarySection } from './BookingSummarySection';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { BookingSummaryPromotions } from './BookingSummaryPromotions';
import { BadgeTag } from '../../../../../account/ui/shared/BadgeTag';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';
import themed from '../../../../../../ui/themer/themed';
import Responsive from '../../../../../../ui/utils/Responsive';
import { IBookingSummarySubmitProps } from './IBookingSummarySubmitProps';
import { getShouldProceedWithoutPay } from '../../../../domain/booking/models/helpers/getShouldProceedWithoutPay';
import { getDifference } from '../../../../../shared/i18n/price/utils/getDifference';

interface IBookingSummaryMobileFooterProps {
	originalTotalPrice?: IPrice;
	totalPrice: IPrice;
	loyaltyPoints?: number;
	Submit?: ComponentType<IBookingSummarySubmitProps>;
	booking: INewBooking;
	changeButtonUrl?: string;
}

export const BookingSummaryMobileFooter = ({
	originalTotalPrice,
	loyaltyPoints,
	totalPrice,
	Submit,
	booking,
	changeButtonUrl,
}: IBookingSummaryMobileFooterProps) => {
	const { t } = useTranslation();
	const { isAuthenticated } = useAuthStore();
	const [showDetails, setShowDetails] = useState(false);
	const shouldProceedWithoutPay = getShouldProceedWithoutPay(
		originalTotalPrice,
		totalPrice,
	);

	const onDetailsClick = () => {
		setShowDetails(!showDetails);
	};

	const discount = booking.parkingProduct?.price.discount;
	const priceDifference =
		originalTotalPrice && getDifference(totalPrice, originalTotalPrice);
	const isRefund = priceDifference && priceDifference?.centAmount < 0;

	const headerButton = {
		label: showDetails
			? t('web_booking_summary_hide_details')
			: t('web_booking_summary_show_details'),
		onClick: onDetailsClick,
	};

	return (
		<>
			<StyledBookingSummaryMobileFooter>
				<BookingSummaryHeader button={headerButton} />

				{showDetails && (
					<>
						<ParkingProductSummary
							booking={booking}
							changeButtonUrl={changeButtonUrl}
						>
							<AncillariesSummary ancillaries={booking.ancillaries} />

							{booking.promoCode && !!discount && Boolean(+discount) && (
								<BookingSummaryPromotions
									promoCode={booking.promoCode}
									discount={discount}
								/>
							)}

							{originalTotalPrice && (
								<OriginalAndNewTotalMobile
									originalTotalPrice={originalTotalPrice!}
									totalPrice={totalPrice}
								/>
							)}
						</ParkingProductSummary>
					</>
				)}

				{isRefund ? (
					<RefundTotal priceDifference={priceDifference} />
				) : (
					<GrandTotal
						totalPrice={totalPrice}
						priceDifference={priceDifference}
					/>
				)}

				{Submit && <Submit shouldProceedWithoutPay={shouldProceedWithoutPay} />}
				{isAuthenticated && !!loyaltyPoints && (
					<StyledEarningLoyaltyPoints>
						<span>{t('loyalty_booking_summary_earning_label')}</span>
						<BadgeTag
							text={t('loyalty_offer_points_template', {
								points: loyaltyPoints,
							})}
						/>
						<span>
							{t('loyalty_booking_summary_earning_with_booking_label')}
						</span>
					</StyledEarningLoyaltyPoints>
				)}
			</StyledBookingSummaryMobileFooter>
		</>
	);
};

export const StyledBookingSummaryMobileFooter = styled(
	StyledBookingSummaryFooter,
)<{}, ITheme>`
	display: none;
	background: ${themed(({ background }) => background.light)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: flex;
		padding: 16px;
		align-items: stretch;
		flex-direction: column;
		position: fixed;
		bottom: 0;
		width: 100%;
	}

	${StyledBookingSummaryHeader} {
		padding-bottom: 16px;
	}

	${StyledGrandTotal} {
		flex-grow: 1;
		font-size: 16px;
		margin-top: 16px;
		margin-bottom: 16px;
	}

	${StyledOriginalAndNewTotalMobile} {
		flex-basis: 100%;
		flex-shrink: 0;
	}

	${StyledBookingSummarySection} {
		padding: 16px 0;
	}
`;
